import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import DateField from '../../components/Shared/DateField';
import { listAllProceduri, updateProcedura } from '../../services/proceduri';
import { listClienti } from '../../services/clienti';
import { updateBrand } from '../../services/brands';
import { FilterPill, FilterClearBtn, FilterCountHint } from '../../components/Shared/FilterPill';
import '../Clienti/ClientiPage.css';
import './ReinnouriPage.css';

/* ── Constants ── */
const RENEWAL_TIPS = ['marca_osim', 'marca_euipo', 'marca_wipo', 'marca_internationala', 'idepot', 'brevet', 'dmi'];

/* Extract registration office from procedure data */
function extractOficiu(tipProc, data) {
  switch (tipProc) {
    case 'marca_osim':           return data.oficiu || 'OSIM';
    case 'marca_euipo':          return 'EUIPO';
    case 'marca_wipo':           return data.oficiuBaza || 'WIPO';
    case 'marca_internationala': return data.oficiu || '';
    case 'idepot':               return 'BOIP';
    case 'brevet':               return (data.oficiu || 'osim').toUpperCase();
    case 'dmi':                  return (data.oficiu || 'osim').toUpperCase();
    default:                     return '';
  }
}

/* Extract registration/application number from procedure data */
function extractNumar(tipProc, data) {
  if (tipProc === 'brevet') return data.numarCerere || data.numar || '';
  if (tipProc === 'marca_internationala') return data.numar || data.numCerere || '';
  return data.numar || '';
}

/* ── Urgency helpers ── */
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const exp = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((exp - now) / 86400000);
}

function urgency(days) {
  if (days === null) return 'no-date';
  if (days < 0)    return 'expirat';
  if (days <= 30)  return 'urgent';
  if (days <= 90)  return 'curand';
  return 'ok';
}

const URG_LABEL = { expirat: 'Expirat', urgent: 'Sub 30 zile', curand: 'Sub 90 zile', ok: 'OK', 'no-date': '—' };
const URG_SORT  = { expirat: 0, urgent: 1, curand: 2, ok: 3, 'no-date': 4 };

function fmtDate(d) {
  if (!d) return '—';
  const [y, m, z] = d.split('-');
  return `${z}.${m}.${y}`;
}

/* ── Toast ── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg) => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

export default function ReinnouriPage() {
  const { viewMode } = useAuth();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOficiu, setFilterOficiu] = useState('');
  const [filterUrg,    setFilterUrg]    = useState('');
  const [openId,  setOpenId]  = useState(null);
  const [draft,   setDraft]   = useState({ anuntat: false, depusa: false, dataNoua: '' });
  const [saving,  setSaving]  = useState(false);
  const { toasts, show: showToast } = useToast();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      listAllProceduri(RENEWAL_TIPS),
      listClienti(),
    ]).then(([procData, clientiData]) => {
      if (cancelled) return;

      const result = [];

      // From procedures
      for (const p of (procData.proceduri || [])) {
        const d = p.data || {};
        if (!d.dataInregistrare && !d.dataExpirare) continue;
        result.push({
          id:                        `proc-${p.id}`,
          procId:                    p.id,
          procData:                  d,
          denumire:                  d.denumire || d.numar || d.numarDepozit || p.tip_proc,
          client:                    p.client_name || '—',
          oficiu:                    extractOficiu(p.tip_proc, d),
          numar:                     extractNumar(p.tip_proc, d),
          dataInregistrare:          d.dataInregistrare || null,
          dataExpirare:              d.dataExpirare || null,
          reinnoire_anuntat_client:  d.reinnoire_anuntat_client || false,
          reinnoire_depusa:          d.reinnoire_depusa || false,
          reinnoire_ciclu_expirare:  d.reinnoire_ciclu_expirare || null,
        });
      }

      // From brands — skip brands backed by a procedure, since that procedure is
      // already added above (every marca_osim/euipo/wipo/internationala, idepot,
      // brevet and dmi procedure auto-syncs its own Brand row with the same dates;
      // without this check the same renewal showed up twice, as "proc-X" and "brand-X").
      for (const client of (clientiData.clients || [])) {
        for (const brand of (client.brands || [])) {
          if (brand.procedura_id) continue;
          if (!brand.data_inregistrare && !brand.data_expirare) continue;
          result.push({
            id:                        `brand-${brand.id}`,
            brandId:                   brand.id,
            brandData:                 brand,
            denumire:                  brand.name,
            client:                    client.nume,
            oficiu:                    brand.oficiu_inreg || '',
            numar:                     brand.nr_inregistrare || '',
            dataInregistrare:          brand.data_inregistrare || null,
            dataExpirare:              brand.data_expirare || null,
            reinnoire_anuntat_client:  brand.reinnoire_anuntat_client || false,
            reinnoire_depusa:          brand.reinnoire_depusa || false,
            reinnoire_ciclu_expirare:  brand.reinnoire_ciclu_expirare || null,
          });
        }
      }

      // Sort: expired first, then by days remaining, no-date last
      result.sort((a, b) => {
        const da = daysUntil(a.dataExpirare);
        const db = daysUntil(b.dataExpirare);
        const ua = URG_SORT[urgency(da)];
        const ub = URG_SORT[urgency(db)];
        if (ua !== ub) return ua - ub;
        if (da !== null && db !== null) return da - db;
        return 0;
      });

      setRows(result);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) { showToast('Eroare la incarcare date'); setLoading(false); }
    });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  function handleRowClick(r) {
    if (openId === r.id) {
      setOpenId(null);
      return;
    }
    setOpenId(r.id);
    setDraft({
      anuntat:  r.reinnoire_anuntat_client,
      depusa:   r.reinnoire_depusa,
      dataNoua: r.dataExpirare || '',
    });
  }

  // When user checks/unchecks "depusa" in the panel
  function handleDepusaChange(checked) {
    setDraft(d => ({
      ...d,
      depusa: checked,
    }));
  }

  // ciclu_expirare = dataNoua when depusa=true (so cicluSatisfied=true after save)
  function deriveCiclu(depusa, dataNoua) {
    return depusa && dataNoua ? dataNoua : null;
  }

  async function handleSave(r) {
    setSaving(true);
    const ciclu = deriveCiclu(draft.depusa, draft.dataNoua);
    try {
      if (r.id.startsWith('proc-')) {
        const newData = {
          ...r.procData,
          dataExpirare:              draft.dataNoua || '',
          reinnoire_anuntat_client:  draft.anuntat,
          reinnoire_depusa:          draft.depusa,
          reinnoire_ciclu_expirare:  ciclu,
        };
        await updateProcedura(r.procId, { data: newData });
        setRows(prev => prev.map(x => x.id !== r.id ? x : {
          ...x,
          dataExpirare:              draft.dataNoua || null,
          reinnoire_anuntat_client:  draft.anuntat,
          reinnoire_depusa:          draft.depusa,
          reinnoire_ciclu_expirare:  ciclu,
          procData:                  newData,
        }));
      } else {
        const brandPayload = {
          ...r.brandData,
          data_expirare:             draft.dataNoua || null,
          reinnoire_anuntat_client:  draft.anuntat,
          reinnoire_depusa:          draft.depusa,
          reinnoire_ciclu_expirare:  ciclu,
        };
        await updateBrand(r.brandId, brandPayload);
        setRows(prev => prev.map(x => x.id !== r.id ? x : {
          ...x,
          dataExpirare:              draft.dataNoua || null,
          reinnoire_anuntat_client:  draft.anuntat,
          reinnoire_depusa:          draft.depusa,
          reinnoire_ciclu_expirare:  ciclu,
          brandData:                 brandPayload,
        }));
      }
      setOpenId(null);
      showToast('Salvat cu succes');
    } catch {
      showToast('Eroare la salvare');
    } finally {
      setSaving(false);
    }
  }

  const filtered = rows.filter(r => {
    if (filterOficiu && r.oficiu !== filterOficiu) return false;
    const days = daysUntil(r.dataExpirare);
    if (filterUrg && urgency(days) !== filterUrg) return false;
    return true;
  });

  const hasFilters = !!(filterOficiu || filterUrg);
  const oficiiOptions = [...new Set(rows.map(r => r.oficiu).filter(Boolean))].sort();

  return (
    <div className="page-wrap">
      <div className="page-header">
        <div>
          <div className="page-title">Reinnoiri</div>
          <div className="page-sub">Urmarire date inregistrare si expirare proceduri IP</div>
        </div>
      </div>

      <div className="filter-bar">
        <FilterPill searchable value={filterOficiu} onChange={e => setFilterOficiu(e.target.value)}>
          <option value="">Toate oficiile</option>
          {oficiiOptions.map(o => <option key={o} value={o}>{o}</option>)}
        </FilterPill>
        <FilterPill value={filterUrg} onChange={e => setFilterUrg(e.target.value)}>
          <option value="">Toate statusurile</option>
          <option value="expirat">Expirat</option>
          <option value="urgent">Sub 30 zile</option>
          <option value="curand">Sub 90 zile</option>
          <option value="ok">OK</option>
          <option value="no-date">Fara data expirare</option>
        </FilterPill>
        {hasFilters && <FilterClearBtn onClick={() => { setFilterOficiu(''); setFilterUrg(''); }} />}
        <FilterCountHint count={filtered.length} singular="intrare" plural="intrari" />
      </div>

      {loading ? (
        <div className="clients-card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>Se incarca...</div>
      ) : filtered.length === 0 ? (
        <div className="clients-card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          {rows.length === 0 ? 'Nu exista proceduri cu date de inregistrare / expirare.' : 'Niciun rezultat pentru filtrele selectate.'}
        </div>
      ) : (
        <div className="clients-card">
          <table className="clients-table">
            <thead>
              <tr>
                <th>Denumire</th>
                <th>Client</th>
                <th>Oficiu</th>
                <th>Nr. Inregistrare</th>
                <th>Data inregistrare</th>
                <th>Data expirare</th>
                <th>Zile ramase</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const days  = daysUntil(r.dataExpirare);
                const urg   = urgency(days);
                const isOpen = openId === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      className={`rn-row${isOpen ? ' rn-row-open' : ''}`}
                      onClick={() => handleRowClick(r)}
                    >
                      <td className="rn-denumire">{r.denumire}</td>
                      <td className="rn-client">{r.client}</td>
                      <td>{r.oficiu ? <span className="rn-tip-badge">{r.oficiu}</span> : <span className="rn-empty">—</span>}</td>
                      <td className="rn-numar">{r.numar || '—'}</td>
                      <td>{fmtDate(r.dataInregistrare)}</td>
                      <td className={urg === 'expirat' ? 'rn-date-expirat' : ''}>{fmtDate(r.dataExpirare)}</td>
                      <td className="rn-days">
                        {days === null ? '—' : days < 0 ? `${Math.abs(days)} zile expirat` : `${days} zile`}
                      </td>
                      <td>
                        <span className={`rn-urg-badge rn-urg-${urg}`}>{URG_LABEL[urg]}</span>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr className="rn-panel-row">
                        <td colSpan={8}>
                          <div className="rn-panel">
                            <div className="rn-panel-fields">
                              <label className="rn-check-label">
                                <input
                                  type="checkbox"
                                  checked={draft.anuntat}
                                  onChange={e => setDraft(d => ({ ...d, anuntat: e.target.checked }))}
                                />
                                Anuntat clientul
                              </label>
                              <label className="rn-check-label">
                                <input
                                  type="checkbox"
                                  checked={draft.depusa}
                                  onChange={e => handleDepusaChange(e.target.checked)}
                                />
                                Reinnoirea depusa
                              </label>
                              <div className="rn-date-field">
                                <label>Data noua de expirare</label>
                                <DateField
                                  value={draft.dataNoua || ''}
                                  onChange={v => setDraft(d => ({ ...d, dataNoua: v }))}
                                />
                              </div>
                            </div>
                            <div className="rn-panel-actions">
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={e => { e.stopPropagation(); handleSave(r); }}
                                disabled={saving}
                              >
                                {saving ? 'Se salveaza…' : 'Salveaza'}
                              </button>
                              <button
                                className="btn btn-sm"
                                onClick={e => { e.stopPropagation(); setOpenId(null); }}
                                disabled={saving}
                              >
                                Anuleaza
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {toasts.length > 0 && (
        <div className="toast-wrap">
          {toasts.map(t => <div key={t.id} className="toast">{t.msg}</div>)}
        </div>
      )}
    </div>
  );
}
